import { ChatPillData } from '../types/chatTypes';

export type DocumentVisualType = 'file' | 'knowledge-base-resource';

/**
 * Determina el tipo visual de un documento para asignar colores.
 * Todas las pills de documentos usan el mismo gris claro; solo el icono varía por extensión.
 */
export const getDocumentVisualType = (
  data: Partial<ChatPillData>
): DocumentVisualType => {
  const { isCategory, type } = data;
  if (isCategory || type === 'category' || type === 'knowledge-base-resource') {
    return 'knowledge-base-resource';
  }
  return 'file';
};

/**
 * Retorna el path del icono basado solo en la extensión del archivo.
 * Para categorías/carpetas (pills que no son archivos) se puede pasar type 'category' o 'knowledge-base-resource'.
 */
export const getIconPath = (name: string, type?: string) => {
  if (type === 'category' || type === 'knowledge-base-resource') {
    return 'assets/icons/file-folder-icon.svg';
  }

  const ext = name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf': return 'assets/icons/pdf-icon.svg';
    case 'docx':
    case 'doc': return 'assets/icons/microsoft-word-icon.svg';
    case 'xlsx':
    case 'xls': return 'assets/icons/microsoft-excel-logo.svg';
    default: return 'assets/icons/file-text-icon.svg';
  }
};

/**
 * Retorna todas las propiedades necesarias para renderizar una pill de forma unificada.
 */
export const getPillProps = (data: Partial<ChatPillData>) => {
  const visualType = getDocumentVisualType(data);
  const styles = getDocumentStyleClasses(visualType);
  const iconPath = getIconPath(data.name || '', data.type || visualType);
  
  return {
    visualType,
    styles,
    iconPath,
    label: data.name || 'Document'
  };
};

/**
 * Retorna las clases de Tailwind para el estilo del documento
 */
export const getDocumentStyleClasses = (visualType: DocumentVisualType) => {
  // Estilo Emerald para KB/Categorías, Slate para Archivos
  if (visualType === 'knowledge-base-resource') {
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
      hover: "hover:bg-emerald-100",
      hex: {
        bg: '#ecfdf5', // emerald-50
        border: '#a7f3d0', // emerald-200
        text: '#047857', // emerald-700
        hover: '#d1fae5' // emerald-100
      }
    };
  }

  return {
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200",
    hover: "hover:bg-slate-200",
    hex: {
      bg: '#f1f5f9', // slate-100
      border: '#e2e8f0', // slate-200
      text: '#334155', // slate-700
      hover: '#cbd5e1' // slate-300
    }
  };
};
